import { useId, useState, type InputHTMLAttributes, type ReactNode } from 'react'
import { EyeIcon, EyeOffIcon } from './icons'
import styles from './Field.module.css'

interface Props extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'type'> {
  label?: string
  /** Подпись под полем. Не исчезает при ошибке — они про разное. */
  note?: ReactNode
  /** Текст ошибки. Пусто — поле в норме. */
  error?: string | null
  /** Иконка слева. */
  icon?: ReactNode
  /** Короткая подпись справа внутри поля: валюта, единица. */
  param?: string | undefined
  type?: 'text' | 'email' | 'password' | 'number' | 'tel'
  /** Моноширинные цифры: для сумм и кодов. */
  numeric?: boolean
  /**
   * С какой ширины экрана подпись видна. Ниже — она остаётся в разметке
   * для читалки, но не занимает места: в макете у фильтров на телефоне
   * подписей нет, их роль играет плейсхолдер.
   */
  labelFrom?: 768 | 1280
}

/**
 * Поле ввода из макета. Состояния Idle / Hover / Active / Filled /
 * Error / Disabled — все, кроме Error, чисто визуальные.
 *
 * У пароля своя кнопка показа: вводить длинный пароль вслепую на
 * телефоне — верный способ ошибиться трижды и получить блокировку
 * по числу попыток.
 */
export function Field({
  label, note, error, icon, param, numeric, labelFrom, type = 'text', disabled, id, ...rest
}: Props) {
  const autoId = useId()
  const fieldId = id ?? autoId
  const [revealed, setRevealed] = useState(false)

  const isPassword = type === 'password'
  const inputType = isPassword && revealed ? 'text' : type

  const control = [styles.control]
  if (error) control.push(styles.invalid)
  if (disabled) control.push(styles.disabled)

  const input = [styles.input]
  if (numeric) input.push(styles.numeric)

  return (
    <div className={styles.field} data-label-from={labelFrom}>
      {label ? <label className={styles.label} htmlFor={fieldId}>{label}</label> : null}

      <div className={control.join(' ')}>
        {icon ? <span className={styles.slot}>{icon}</span> : null}
        <input
          {...rest}
          id={fieldId}
          type={inputType}
          disabled={disabled}
          className={input.join(' ')}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${fieldId}-error` : note ? `${fieldId}-note` : undefined}
        />
        {param ? <span className={styles.param}>{param}</span> : null}
        {isPassword ? (
          <button
            type="button"
            className={styles.reveal}
            onClick={() => setRevealed((v) => !v)}
            aria-label={revealed ? 'Скрыть пароль' : 'Показать пароль'}
          >
            {revealed ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        ) : null}
      </div>

      {error ? (
        <span className={styles.error} id={`${fieldId}-error`} role="alert">{error}</span>
      ) : null}
      {note ? <span className={styles.note} id={`${fieldId}-note`}>{note}</span> : null}
    </div>
  )
}
